import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import io from 'socket.io-client';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Import components
import Navigation from './components/Navigation';
import Homepage from './components/Homepage';
import MarketView from './components/MarketView';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Trading from './components/Trading';
import Wallet from './components/Wallet';
import Deposit from './components/Deposit';
import Withdrawal from './components/Withdrawal';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import KYCVerification from './components/KYCVerification';
import AdminDashboard from './components/AdminDashboard';
import UserManagement from './components/UserManagement';
import OrderManagement from './components/OrderManagement';
import DepositManagement from './components/DepositManagement';
import WithdrawalManagement from './components/WithdrawalManagement';
import KYCPending from './components/KYCPending';
import ChartView from './components/ChartView';
import AdminChat from './components/AdminChat';
import RefreshToken from './components/RefreshToken';
import UserProfile from './components/UserProfile';
import VerifyEmail from './components/VerifyEmail';
import ChangeEmail from './components/ChangeEmail';
import KycGate from './components/KycGate';
import './App.css';

// Mobile detection with enhanced checking
const isMobile = /iPhone|iPad|iPod|Android|BlackBerry|Windows Phone|webOS/i.test(navigator.userAgent);
const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isAndroid = /Android/i.test(navigator.userAgent);

// Production configuration
const isDevelopment = process.env.NODE_ENV === 'development';
const BACKEND_URL = isDevelopment 
  ? 'https://trcsite.net' 
  : 'https://trcsite.net';
const API_BASE_URL = isDevelopment
  ? 'https://trcsite.net/api'
  : 'https://trcsite.net/api';

console.log('📱 Mobile Device:', isMobile);
console.log('🍎 iOS Device:', isIOS);
console.log('🤖 Android Device:', isAndroid);
console.log('✨ Environment:', process.env.NODE_ENV);
console.log('🔗 Backend URL:', BACKEND_URL);
console.log('📡 API Base URL:', API_BASE_URL);

// Enhanced Socket.IO config for mobile with domain support
const socketConfig = {
  transports: ['websocket', 'polling'],
  timeout: 20000,
  reconnection: true,
  reconnectionAttempts: 20,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 15000,
  autoConnect: false, // We'll connect manually after auth
  forceNew: true,
  multiplex: false,
  withCredentials: true,
  path: '/socket.io/',
  extraHeaders: isMobile ? {
    'X-Device-Type': 'mobile',
    'X-Client-Version': '1.0.0'
  } : {}
};

// Create socket instance
const createSocket = () => io(BACKEND_URL, socketConfig);

// Mobile Error Boundary with enhanced error handling
class MobileErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('📱 Mobile Error Caught:', error);
    console.error('📱 Error Info:', errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Enhanced error logging for mobile
    this.logMobileError(error, errorInfo);
  }

  logMobileError = async (error, errorInfo) => {
    const errorData = {
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      isMobile: isMobile,
      isIOS: isIOS,
      isAndroid: isAndroid,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      memory: navigator.deviceMemory || 'unknown'
    };

    try {
      // Try to send to backend
      await fetch(`${API_BASE_URL}/log-client-error`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Client-Error': 'true'
        },
        body: JSON.stringify(errorData)
      });
    } catch (fetchError) {
      // Fallback: log to console
      console.error('📱 Error logging failed:', fetchError);
      console.error('📱 Client Error Data:', errorData);
    }
  };

  handleRetry = () => {
    this.setState(prevState => ({
      retryCount: prevState.retryCount + 1,
      hasError: false,
      error: null,
      errorInfo: null
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="mobile-error-boundary">
          <div className="error-content">
            <div className="error-icon">⚠️</div>
            <h2>Oops! Something went wrong</h2>
            <p>We encountered an unexpected error. Our team has been notified.</p>
            
            {isMobile && (
              <div className="mobile-tips">
                <h4>📱 Mobile Tips:</h4>
                <ul>
                  <li>Check your internet connection</li>
                  <li>Close other apps running in background</li>
                  <li>Update your browser to latest version</li>
                  {isIOS && <li>Try using Safari browser</li>}
                  {isAndroid && <li>Try using Chrome browser</li>}
                </ul>
              </div>
            )}
            
            <div className="error-actions">
              <button 
                onClick={this.handleRetry}
                className="retry-btn"
                disabled={this.state.retryCount >= 3}
              >
                {this.state.retryCount >= 3 ? 'Max Retries Reached' : 'Try Again'}
              </button>
              <button 
                onClick={this.handleReload}
                className="reload-btn"
              >
                Reload App
              </button>
              <button 
                onClick={this.handleHome}
                className="home-btn"
              >
                Go to Homepage
              </button>
            </div>
            
            {this.state.retryCount >= 2 && (
              <div className="persistent-error">
                <p>Still having issues? Try:</p>
                <ol>
                  <li>Clear browser cache and cookies</li>
                  <li>Restart your device</li>
                  <li>Contact support if problem persists</li>
                </ol>
              </div>
            )}

            {isDevelopment && (
              <details className="error-details">
                <summary>Error Details (Development)</summary>
                <p><strong>Error:</strong> {this.state.error && this.state.error.toString()}</p>
                <pre><strong>Stack:</strong> {this.state.error?.stack}</pre>
                <pre><strong>Component Stack:</strong> {this.state.errorInfo?.componentStack}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Global error handler component
function GlobalErrorHandler({ children }) {
  const [globalError, setGlobalError] = useState(null);

  useEffect(() => {
    const handleGlobalError = (event) => {
      console.error('🌍 Global Error:', event.error);
      setGlobalError({
        message: event.error?.message || 'Unknown error occurred',
        stack: event.error?.stack
      });
      
      // Prevent default error handling
      event.preventDefault();
    };

    const handleUnhandledRejection = (event) => {
      console.error('🌍 Unhandled Promise Rejection:', event.reason);
      setGlobalError({
        message: event.reason?.message || 'Promise rejection occurred',
        stack: event.reason?.stack
      });
      
      // Prevent default error handling
      event.preventDefault();
    };

    // Network error handling
    const handleOffline = () => {
      setGlobalError({
        message: 'You are currently offline. Please check your internet connection.',
        type: 'offline'
      });
    };

    const handleOnline = () => {
      if (globalError?.type === 'offline') {
        setGlobalError(null);
        window.location.reload();
      }
    };

    // Register event listeners
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [globalError]);

  const clearError = () => {
    setGlobalError(null);
  };

  if (globalError) {
    return (
      <div className="global-error-page">
        <div className="error-content">
          <div className="error-icon">🚨</div>
          <h1>Application Error</h1>
          <p>{globalError.message}</p>
          
          {globalError.type === 'offline' && (
            <div className="offline-tips">
              <h4>Connection Tips:</h4>
              <ul>
                <li>Check your Wi-Fi or mobile data</li>
                <li>Try turning airplane mode on/off</li>
                <li>Move to a location with better signal</li>
              </ul>
            </div>
          )}
          
          <div className="error-actions">
            <button onClick={clearError} className="continue-btn">
              Try to Continue
            </button>
            <button onClick={() => window.location.reload()} className="reload-btn">
              Reload App
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

function App() {
  const { user, loading, token } = useAuth();
  const [marketData, setMarketData] = useState({});
  const [selectedPair, setSelectedPair] = useState('BTC/USDT');
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [appError, setAppError] = useState(null);
  const socketRef = useRef(null);

  // Enhanced Socket.IO connection management
  useEffect(() => {
    console.log('🔌 Initializing Socket.IO connection...');

    const initializeSocket = () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      socketRef.current = createSocket();

      const handleConnect = () => {
        console.log('✅ Connected to server');
        setConnectionStatus('connected');
        setAppError(null);
        
        // Authenticate socket if user is logged in
        if (token) {
          socketRef.current.emit('authenticate', { token });
        }
      };

      const handleAuthenticated = (data) => {
        console.log('✅ Socket authenticated:', data);
      };

      const handleAuthenticationError = (error) => {
        console.error('❌ Socket authentication error:', error);
      };

      const handleConnectError = (error) => {
        console.error('❌ Socket connection error:', error);
        setConnectionStatus('error');
        
        // Mobile-specific connection retry with backoff
        if (isMobile) {
          setTimeout(() => {
            if (socketRef.current) {
              socketRef.current.connect();
            }
          }, 3000);
        }
      };

      const handleDisconnect = (reason) => {
        console.log('🔌 Disconnected:', reason);
        setConnectionStatus('disconnected');
        
        if (reason === 'io server disconnect') {
          // Server initiated disconnect, try to reconnect
          setTimeout(() => {
            if (socketRef.current) {
              socketRef.current.connect();
            }
          }, 2000);
        }
      };

      const handleReconnect = (attempt) => {
        console.log(`🔄 Reconnected on attempt ${attempt}`);
        setConnectionStatus('connected');
      };

      const handleReconnectAttempt = (attempt) => {
        console.log(`🔄 Reconnection attempt ${attempt}`);
        setConnectionStatus('reconnecting');
      };

      const handleReconnectFailed = () => {
        console.error('❌ Reconnection failed');
        setConnectionStatus('error');
      };

      const handleMarketData = (data) => {
        setMarketData(prev => ({ ...prev, ...data }));
      };

      const handleMobileDetected = (data) => {
        console.log('📱 Mobile optimization active:', data);
      };

      // Register all event listeners
      socketRef.current.on('connect', handleConnect);
      socketRef.current.on('authenticated', handleAuthenticated);
      socketRef.current.on('authentication_error', handleAuthenticationError);
      socketRef.current.on('connect_error', handleConnectError);
      socketRef.current.on('disconnect', handleDisconnect);
      socketRef.current.on('reconnect', handleReconnect);
      socketRef.current.on('reconnect_attempt', handleReconnectAttempt);
      socketRef.current.on('reconnect_failed', handleReconnectFailed);
      socketRef.current.on('marketData', handleMarketData);
      socketRef.current.on('mobile_detected', handleMobileDetected);

      // Initialize connection
      socketRef.current.connect();
    };

    initializeSocket();

    return () => {
      // Cleanup
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [token]);

  // Reconnect socket when token changes
  useEffect(() => {
    if (token && socketRef.current) {
      socketRef.current.emit('authenticate', { token });
    }
  }, [token]);

  // Enhanced mobile network monitoring
  useEffect(() => {
    if (!isMobile) return;

    const handleOnline = () => {
      console.log('📱 Mobile: Back online');
      setConnectionStatus('reconnecting');
      if (socketRef.current) {
        socketRef.current.connect();
      }
    };

    const handleOffline = () => {
      console.log('📱 Mobile: Went offline');
      setConnectionStatus('offline');
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && socketRef.current?.disconnected) {
        console.log('📱 Mobile: App became visible, reconnecting...');
        socketRef.current.connect();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Show global app error
  if (appError) {
    return (
      <div className="global-error-page">
        <div className="error-content">
          <h1>Application Error</h1>
          <p>{appError}</p>
          <div className="error-actions">
            <button onClick={() => window.location.reload()} className="retry-btn">
              Reload App
            </button>
            <button onClick={() => setAppError(null)} className="continue-btn">
              Try to Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced connection error handling
  if (connectionStatus === 'error') {
    return (
      <div className="connection-error-page">
        <div className="error-content">
          <div className="error-icon">🔌</div>
          <h1>Connection Issue</h1>
          <p>Unable to connect to TRC Crypto Trading Platform.</p>
          
          {isMobile ? (
            <div className="mobile-connection-tips">
              <h4>📱 Mobile Connection Tips:</h4>
              <ul>
                <li>Check your internet connection (Wi-Fi/Mobile Data)</li>
                <li>Restart the app or browser</li>
                <li>Try switching between Wi-Fi and mobile data</li>
                <li>Clear browser cache if problem persists</li>
              </ul>
            </div>
          ) : (
            <div className="desktop-connection-tips">
              <h4>💻 Desktop Connection Tips:</h4>
              <ul>
                <li>Check your internet connection</li>
                <li>Disable VPN or proxy if used</li>
                <li>Try refreshing the page</li>
                <li>Check firewall settings</li>
              </ul>
            </div>
          )}
          
          <div className="connection-actions">
            <button onClick={() => window.location.reload()} className="retry-btn">
              Retry Connection
            </button>
            <button onClick={() => setConnectionStatus('connecting')} className="reconnect-btn">
              Try Reconnect
            </button>
          </div>
          
          <div className="connection-status">
            <p>Backend: {BACKEND_URL}</p>
            <p>Status: {connectionStatus}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading TRC Crypto Platform...</p>
        {connectionStatus !== 'connected' && (
          <div className="connection-status">
            <small>Status: {connectionStatus}</small>
            {connectionStatus === 'reconnecting' && (
              <div className="reconnecting-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </div>
            )}
          </div>
        )}
        {isMobile && (
          <div className="mobile-loading-info">
            <small className="mobile-indicator">📱 Mobile Mode</small>
            {isIOS && <small>iOS Optimized</small>}
            {isAndroid && <small>Android Optimized</small>}
          </div>
        )}
      </div>
    );
  }

  return (
    <MobileErrorBoundary>
      <GlobalErrorHandler>
        <Router>
          <div className={`App ${!user ? 'dark-theme' : ''} ${isMobile ? 'mobile-app' : 'desktop-app'}`}>
            {user && (
              <Navigation 
                socket={socketRef.current} 
                connectionStatus={connectionStatus} 
                isMobile={isMobile}
              />
            )}
            
            <main className={`main-content ${isMobile ? 'mobile-content' : 'desktop-content'}`}>
              <Routes>
                <Route path="/" element={<Homepage marketData={marketData} isLoggedIn={!!user} isMobile={isMobile} />} />
                <Route path="/markets" element={<MarketView marketData={marketData} isMobile={isMobile} />} />
                <Route path="/login" element={!user ? <Login isMobile={isMobile} /> : <Navigate to="/" />} />
                <Route path="/register" element={!user ? <Register isMobile={isMobile} /> : <Navigate to="/" />} />
                <Route path="/forgot-password" element={!user ? <ForgotPassword isMobile={isMobile} /> : <Navigate to="/" />} />
                <Route path="/reset-password" element={!user ? <ResetPassword isMobile={isMobile} /> : <Navigate to="/" />} />
                <Route path="/reset-password/:token" element={!user ? <ResetPassword isMobile={isMobile} /> : <Navigate to="/" />} />
                <Route path="/dashboard" element={user ? <Dashboard marketData={marketData} isMobile={isMobile} /> : <Navigate to="/login" />} />
                <Route path="/trade" element={user ? <KycGate><Trading marketData={marketData} selectedPair={selectedPair} setSelectedPair={setSelectedPair} socket={socketRef.current} isMobile={isMobile} /></KycGate> : <Navigate to="/login" />} />
                <Route path="/wallet" element={user ? <KycGate><Wallet isMobile={isMobile} /></KycGate> : <Navigate to="/login" />} />
                <Route path="/deposit" element={user ? <KycGate><Deposit isMobile={isMobile} /></KycGate> : <Navigate to="/login" />} />
                <Route path="/withdraw" element={user ? <KycGate><Withdrawal isMobile={isMobile} /></KycGate> : <Navigate to="/login" />} />
                <Route path="/kyc-verification" element={user ? <KYCVerification isMobile={isMobile} /> : <Navigate to="/login" />} />
                <Route path="/profile" element={user ? <UserProfile isMobile={isMobile} /> : <Navigate to="/login" />} />
                <Route path="/verify-email" element={<VerifyEmail isMobile={isMobile} />} />
                <Route path="/verify-email-change" element={<ChangeEmail isMobile={isMobile} />} />                


                {/* Admin Routes */}
                <Route path="/admin" element={user?.role === 'admin' ? <AdminDashboard socket={socketRef.current} isMobile={isMobile} /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/users" element={user?.role === 'admin' ? <UserManagement isMobile={isMobile} /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/orders" element={user?.role === 'admin' ? <OrderManagement isMobile={isMobile} /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/deposits" element={user?.role === 'admin' ? <DepositManagement isMobile={isMobile} /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/withdrawals" element={user?.role === 'admin' ? <WithdrawalManagement isMobile={isMobile} /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/kyc" element={user?.role === 'admin' ? <KYCPending isMobile={isMobile} /> : <Navigate to="/dashboard" />} />
                <Route path="/admin/chat" element={user?.role === 'admin' ? <AdminChat socket={socketRef.current} isMobile={isMobile} /> : <Navigate to="/dashboard" />} />
                
                <Route path="/chart" element={<ChartView marketData={marketData} selectedPair={selectedPair} setSelectedPair={setSelectedPair} socket={socketRef.current} isMobile={isMobile} />} />
                <Route path="/refresh-token" element={<RefreshToken isMobile={isMobile} />} />
                
                {/* 404 Page */}
                <Route path="*" element={<NotFoundPage isMobile={isMobile} />} />
              </Routes>
            </main>

            {/* Mobile connection status bar */}
            {isMobile && connectionStatus !== 'connected' && (
              <div className={`mobile-connection-bar ${connectionStatus}`}>
                <span>
                  {connectionStatus === 'connecting' && '🔄 Connecting...'}
                  {connectionStatus === 'reconnecting' && '🔄 Reconnecting...'}
                  {connectionStatus === 'disconnected' && '🔌 Disconnected'}
                </span>
              </div>
            )}
          </div>
        </Router>
      </GlobalErrorHandler>
    </MobileErrorBoundary>
  );
}

// 404 Page Component
function NotFoundPage({ isMobile }) {
  return (
    <div className={`not-found-page ${isMobile ? 'mobile' : 'desktop'}`}>
      <div className="not-found-content">
        <h1>404</h1>
        <p>Page Not Found</p>
        <p>The page you're looking for doesn't exist.</p>
        <a href="/" className="home-link">Go to Homepage</a>
        
        {isMobile && (
          <div className="mobile-suggestions">
            <p>Try these pages instead:</p>
            <div className="suggestion-links">
              <a href="/dashboard">Dashboard</a>
              <a href="/markets">Markets</a>
              <a href="/trade">Trade</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function WrappedApp() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ThemeProvider>
  );
}
